/**
 * Utility functions for input validation and sanitization
 */

/**
 * Configuration for input validation
 */
export interface InputValidationConfig {
  maxLength: number;
  minLength?: number;
  allowEmpty?: boolean;
  trimWhitespace?: boolean;
}

/**
 * Default validation configurations for different input types
 */
export const DEFAULT_VALIDATION_CONFIG = {
  projectName: {
    maxLength: 50,
    minLength: 1,
    allowEmpty: false,
    trimWhitespace: true,
  },
  projectDescription: {
    maxLength: 500,
    allowEmpty: true,
    trimWhitespace: true,
  },
  templateName: {
    maxLength: 50,
    minLength: 1,
    allowEmpty: false,
    trimWhitespace: true,
  },
  templateDescription: {
    maxLength: 200,
    allowEmpty: true,
    trimWhitespace: true,
  },
  email: {
    maxLength: 100,
    minLength: 5,
    allowEmpty: false,
    trimWhitespace: true,
  },
  integrationName: {
    maxLength: 50,
    minLength: 1,
    allowEmpty: false,
    trimWhitespace: true,
  },
  integrationDescription: {
    maxLength: 300,
    allowEmpty: true,
    trimWhitespace: true,
  },
  reportTitle: {
    maxLength: 100,
    minLength: 1,
    allowEmpty: false,
    trimWhitespace: true,
  },
};

/**
 * Validates and sanitizes input based on configuration
 * @param input The input string to validate
 * @param config Validation configuration
 * @returns The sanitized input string
 */
export function validateAndSanitizeInput(
  input: string,
  config: InputValidationConfig
): string {
  // Handle empty input
  if (!input && !config.allowEmpty) {
    return "";
  }

  // Create a copy of the input to work with
  let sanitizedInput = input;

  // Trim whitespace if configured - only for validation purposes, not for display
  // This ensures spaces are preserved in the actual input value
  if (config.trimWhitespace && (sanitizedInput.startsWith(' ') || sanitizedInput.endsWith(' '))) {
    sanitizedInput = sanitizedInput.trim();
  }

  // Truncate if longer than maxLength
  if (sanitizedInput.length > config.maxLength) {
    sanitizedInput = sanitizedInput.substring(0, config.maxLength);
  }

  return sanitizedInput;
}

/**
 * Checks if input is valid according to configuration
 * @param input The input string to validate
 * @param config Validation configuration
 * @returns Whether the input is valid
 */
export function isInputValid(
  input: string,
  config: InputValidationConfig
): boolean {
  // Check if empty is allowed
  if (!input) {
    return !!config.allowEmpty;
  }

  // Check minimum length
  if (config.minLength && input.trim().length < config.minLength) {
    return false;
  }

  // Check maximum length
  if (input.length > config.maxLength) {
    return false;
  }

  return true;
}

/**
 * Custom hook for input validation with React
 * @param initialValue Initial input value
 * @param validationConfig Validation configuration
 * @returns Object with value, setValue, isValid, and errorMessage
 */
export function useInputValidation(
  initialValue: string,
  validationConfig: InputValidationConfig
) {
  const [value, setValue] = React.useState(initialValue);
  const [isTouched, setIsTouched] = React.useState(false);
  
  const sanitizedValue = validateAndSanitizeInput(value, validationConfig);
  const isValid = isInputValid(sanitizedValue, validationConfig);
  
  let errorMessage = "";
  if (!isValid && isTouched) {
    if (!sanitizedValue && !validationConfig.allowEmpty) {
      errorMessage = "This field is required";
    } else if (
      validationConfig.minLength &&
      sanitizedValue.length < validationConfig.minLength
    ) {
      errorMessage = `Must be at least ${validationConfig.minLength} characters`;
    } else if (sanitizedValue.length > validationConfig.maxLength) {
      errorMessage = `Must be at most ${validationConfig.maxLength} characters`;
    }
  }
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value);
    setIsTouched(true);
  };
  
  const handleBlur = () => {
    setIsTouched(true);
    // Don't replace the value with sanitizedValue on blur
    // This would remove spaces in the middle of text
    // Only trim if configured and there are leading/trailing spaces
    if (validationConfig.trimWhitespace && (value.startsWith(' ') || value.endsWith(' '))) {
      setValue(value.trim());
    }
  };
  
  return {
    value: sanitizedValue,
    setValue,
    isValid,
    errorMessage,
    handleChange,
    handleBlur,
    isTouched,
  };
}

// Add React import for the hook
import React from 'react';
